<script setup lang="ts">
interface CheckboxOption {
  name: string;
  value: string | number;
  id?: string | number;
}

interface Props {
  title: string;
  options: CheckboxOption[];
  modelValue?: (string | number)[];
  width?: string;
  errorMessage?: string;
  required?: boolean;
  column?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  width: "33%",
  errorMessage: "",
  required: false,
  column: false,
});

const emit = defineEmits<{
  "update:modelValue": [value: (string | number)[]];
}>();

const sortedOptions = computed(() =>
  [...props.options].sort((a, b) => a.name.localeCompare(b.name)),
);

function toggleOption(value: string | number) {
  const current = [...props.modelValue];
  const idx = current.indexOf(value);
  if (idx >= 0) {
    current.splice(idx, 1);
  } else {
    current.push(value);
  }
  emit("update:modelValue", current);
}

function isChecked(value: string | number) {
  return props.modelValue.includes(value);
}
</script>

<template>
  <div class="checkbox-list">
    <p
      v-if="title"
      class="checkbox-list__title"
    >
      {{ title }}
    </p>
    <div :class="['checkbox-list__options', { 'checkbox-list__options--column': column }]">
      <label
        v-for="option in sortedOptions"
        :key="option.value"
        class="checkbox-list__item"
        :style="{ width }"
        @click.prevent="toggleOption(option.value)"
      >
        <span :class="['checkbox-list__box', { checked: isChecked(option.value) }]">
          <fa
            v-if="isChecked(option.value)"
            :icon="['fal', 'check']"
          />
        </span>
        <span>{{ option.name }}</span>
      </label>
    </div>
    <BannerError :message="errorMessage" />
  </div>
</template>

<style lang="scss">
.checkbox-list {
  &__title {
    padding: 10px;
    color: var(--color-grey-1);
  }
  &__options {
    list-style-type: none;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    margin: 0;
    padding: 0;
    &--column {
      flex-direction: column;
    }
  }
  &__item {
    display: flex;
    align-items: center;
    margin: 0.35rem 0;
    cursor: pointer;
    span {
      color: var(--color-grey-1);
      font-size: 0.9rem;
    }
  }
  &__box {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.2rem;
    height: 1.2rem;
    min-width: 1.2rem;
    border: 2px solid var(--color-grey-2);
    border-radius: 0.3rem;
    margin-right: 0.5rem;
    cursor: pointer;
    transition:
      background-color 0.15s,
      border-color 0.15s;
    svg {
      color: var(--color-white-1);
      font-size: 0.7rem;
    }
    &.checked {
      background-color: var(--color-pink-1);
      border-color: var(--color-pink-1);
    }
  }
}
</style>
